import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { AiModelPrice, AiUsageReport, AiUsageSums } from '@neuroedge/shared';
import { Button, Card, Modal, PageState, TextField, useToast } from '../../components/ui';
import {
  DataGrid,
  applyClientGrid,
  useGridState,
  type ClientColumnSpec,
  type GridColumn,
} from '../../components/data-grid/DataGrid';
import { useAuth } from '../../auth/AuthContext';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  useAddAiModelPrice,
  useAiModelPrices,
  useAiUsageReport,
  useDeleteAiModelPrice,
  useSetUsdToInr,
} from '../../api/ai-usage';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

const FEATURE_LABELS: Record<string, string> = {
  QUOTATION_WHATSAPP: 'Quotations from WhatsApp',
  QUOTATION_MANUAL: 'Quotations by staff',
  CATALOG_IMPORT: 'Catalogue import',
};

/** YYYY-MM-DD of a local date. */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

type Preset = 'this-month' | 'last-month' | 'last-7' | 'custom';

function presetPeriod(p: Exclude<Preset, 'custom'>): { from: string; to: string } {
  const now = new Date();
  if (p === 'last-7') {
    return { from: iso(new Date(now.getTime() - 6 * 86_400_000)), to: iso(now) };
  }
  if (p === 'last-month') {
    return {
      from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: iso(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
}

const rupees = (usd: number, rate: number) =>
  (usd * rate).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: usd * rate < 10 ? 2 : 0,
  });
const dollars = (usd: number) =>
  `$${usd.toLocaleString('en-US', { maximumFractionDigits: usd < 1 ? 4 : 2 })}`;
const count = (n: number) => n.toLocaleString('en-IN');
const tokens = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1000
      ? `${(n / 1000).toFixed(1)}k`
      : String(n);

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        minWidth: 0,
      }}
    >
      <div style={muted}>{label}</div>
      <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{value}</div>
      {sub ? <div style={muted}>{sub}</div> : null}
    </div>
  );
}

type TenantRow = AiUsageReport['byTenant'][number];

const TENANT_SPEC: Record<string, ClientColumnSpec<TenantRow>> = {
  tenantName: { text: (t) => t.tenantName },
  calls: { number: (t) => t.calls },
  totalTokens: { number: (t) => t.totalTokens },
  costUsd: { number: (t) => t.costUsd },
};

function ByTenant({ report }: { report: AiUsageReport }) {
  const [grid, setGrid] = useGridState({ sortBy: 'costUsd', sortOrder: 'desc' });
  const rate = report.usdToInr;
  const page = applyClientGrid(report.byTenant, grid, TENANT_SPEC);
  const columns: GridColumn<TenantRow>[] = [
    {
      key: 'tenantName',
      header: 'Workspace',
      sortable: true,
      filter: { type: 'text' },
      render: (t) => (
        <div>
          <strong>{t.tenantName}</strong>
          <div style={muted}>
            {t.planName ?? 'No plan'}
            {t.planMonthlyFee ? ` · ₹${count(t.planMonthlyFee)}/mo` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'calls',
      header: 'AI calls',
      sortable: true,
      align: 'right',
      filter: { type: 'range' },
      render: (t) => (
        <div>
          {count(t.calls)}
          {t.failed ? <div style={muted}>{t.failed} failed</div> : null}
        </div>
      ),
    },
    {
      key: 'totalTokens',
      header: 'Tokens',
      sortable: true,
      align: 'right',
      render: (t) => tokens(t.totalTokens),
    },
    {
      key: 'costUsd',
      header: 'AI cost',
      sortable: true,
      align: 'right',
      render: (t) => (
        <div>
          <strong>{rupees(t.costUsd, rate)}</strong>
          <div style={muted}>
            {dollars(t.costUsd)}
            {t.uncostedCalls ? ` · ${t.uncostedCalls} unpriced` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'share',
      header: 'vs monthly fee',
      align: 'right',
      render: (t) =>
        t.planMonthlyFee && t.planMonthlyFee > 0
          ? `${((t.costUsd * rate * 100) / t.planMonthlyFee).toFixed(1)}%`
          : '—',
    },
  ];
  return (
    <DataGrid
      columns={columns}
      rows={page.rows}
      rowKey={(t) => t.tenantId}
      rowLabel={(t) => t.tenantName}
      total={page.total}
      state={grid}
      onStateChange={setGrid}
      emptyMessage="No AI use in this period."
    />
  );
}

function SumsTable({
  rows,
  label,
  rate,
}: {
  rows: (AiUsageSums & { key: string; name: string })[];
  label: string;
  rate: number;
}) {
  const max = Math.max(...rows.map((r) => r.costUsd), 0);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="ne-table" style={{ minWidth: 520 }}>
        <thead>
          <tr>
            <th>{label}</th>
            <th style={{ textAlign: 'right' }}>Calls</th>
            <th style={{ textAlign: 'right' }}>Tokens</th>
            <th style={{ textAlign: 'right' }}>Cost</th>
            <th style={{ width: '30%' }} aria-label="Share" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{r.name}</td>
              <td style={{ textAlign: 'right' }}>
                {count(r.calls)}
                {r.failed ? <span style={muted}> ({r.failed} failed)</span> : null}
              </td>
              <td style={{ textAlign: 'right' }}>{tokens(r.totalTokens)}</td>
              <td style={{ textAlign: 'right' }}>{rupees(r.costUsd, rate)}</td>
              <td>
                <div
                  style={{
                    height: 8,
                    borderRadius: 4,
                    background: 'var(--color-primary)',
                    width: `${max > 0 ? Math.max((r.costUsd / max) * 100, 2) : 0}%`,
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PRICE_NUMBER = /^\d+(\.\d+)?$/;

function AddPriceModal({ models, onClose }: { models: string[]; onClose: () => void }) {
  const add = useAddAiModelPrice();
  const toast = useToast();
  const [form, setForm] = useState({
    model: models[0] ?? '',
    effectiveFrom: iso(new Date()),
    inputUsdPerMillion: '',
    outputUsdPerMillion: '',
    cachedInputUsdPerMillion: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof form, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => {
      const next = { ...e };
      delete next[k];
      return next;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.model.trim()) next.model = 'Enter the model name.';
    if (!form.effectiveFrom) next.effectiveFrom = 'Choose the date the price applies from.';
    if (!PRICE_NUMBER.test(form.inputUsdPerMillion.trim())) {
      next.inputUsdPerMillion = 'Enter the input price, e.g. 0.30.';
    }
    if (!PRICE_NUMBER.test(form.outputUsdPerMillion.trim())) {
      next.outputUsdPerMillion = 'Enter the output price, e.g. 2.50.';
    }
    if (
      form.cachedInputUsdPerMillion.trim() &&
      !PRICE_NUMBER.test(form.cachedInputUsdPerMillion.trim())
    ) {
      next.cachedInputUsdPerMillion = 'Enter a number, or leave it empty.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      await add.mutateAsync({
        model: form.model.trim(),
        effectiveFrom: form.effectiveFrom,
        inputUsdPerMillion: Number(form.inputUsdPerMillion),
        outputUsdPerMillion: Number(form.outputUsdPerMillion),
        cachedInputUsdPerMillion: form.cachedInputUsdPerMillion.trim()
          ? Number(form.cachedInputUsdPerMillion)
          : null,
      });
      toast.success('Price added - costs are recalculated.');
      onClose();
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) {
        toast.error(err instanceof Error ? err.message : 'Could not add the price.');
      }
    }
  }

  return (
    <Modal title="Add model price" onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        style={{ display: 'grid', gap: 'var(--space-3)' }}
      >
        <p style={{ margin: 0, ...muted }}>
          From Google&apos;s Gemini API price list, in US dollars per 1 million tokens.
          &quot;Thinking&quot; tokens are charged at the output price.
        </p>
        <TextField
          label="Model"
          list="ai-models"
          value={form.model}
          onChange={(e) => set('model', e.target.value)}
          error={errors.model}
        />
        <datalist id="ai-models">
          {models.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        <TextField
          label="Applies from"
          type="date"
          value={form.effectiveFrom}
          onChange={(e) => set('effectiveFrom', e.target.value)}
          error={errors.effectiveFrom}
        />
        <TextField
          label="Input - USD per 1M tokens"
          inputMode="decimal"
          value={form.inputUsdPerMillion}
          onChange={(e) => set('inputUsdPerMillion', e.target.value)}
          error={errors.inputUsdPerMillion}
        />
        <TextField
          label="Output (incl. thinking) - USD per 1M tokens"
          inputMode="decimal"
          value={form.outputUsdPerMillion}
          onChange={(e) => set('outputUsdPerMillion', e.target.value)}
          error={errors.outputUsdPerMillion}
        />
        <TextField
          label="Cached input - USD per 1M tokens (optional)"
          inputMode="decimal"
          value={form.cachedInputUsdPerMillion}
          onChange={(e) => set('cachedInputUsdPerMillion', e.target.value)}
          error={errors.cachedInputUsdPerMillion}
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={add.isPending}>
            {add.isPending ? 'Adding…' : 'Add price'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PricesCard() {
  const prices = useAiModelPrices();
  const del = useDeleteAiModelPrice();
  const setRate = useSetUsdToInr();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [rate, setRateValue] = useState('');
  const [rateError, setRateError] = useState<string | undefined>();

  useEffect(() => {
    if (prices.data) setRateValue(String(prices.data.usdToInr));
  }, [prices.data]);

  if (prices.isPending) return <PageState kind="loading" />;
  if (prices.isError) return <PageState kind="error" message={prices.error.message} />;
  const { prices: rows, modelsInUse } = prices.data;
  const unpriced = modelsInUse.filter((m) => !rows.some((p) => p.model === m));

  async function onDelete(p: AiModelPrice) {
    if (!window.confirm(`Delete the ${p.model} price from ${p.effectiveFrom}?`)) return;
    try {
      await del.mutateAsync(p.id);
      toast.success('Price deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the price.');
    }
  }

  async function onSaveRate() {
    const n = Number(rate);
    if (!rate.trim() || !Number.isFinite(n) || n <= 0) {
      setRateError('Enter rupees per US dollar, e.g. 88.');
      return;
    }
    setRateError(undefined);
    try {
      await setRate.mutateAsync(n);
      toast.success('Rate saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the rate.');
    }
  }

  return (
    <Card title="Model prices">
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        {unpriced.length > 0 ? (
          <p style={{ margin: 0, color: 'var(--color-danger)' }}>
            No price yet for {unpriced.join(', ')} - its calls are counted but not costed.
          </p>
        ) : null}
        <div style={{ overflowX: 'auto' }}>
          <table className="ne-table" style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th>Model</th>
                <th>From</th>
                <th style={{ textAlign: 'right' }}>Input $/1M</th>
                <th style={{ textAlign: 'right' }}>Output $/1M</th>
                <th style={{ textAlign: 'right' }}>Cached $/1M</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={muted}>
                    No prices yet.
                  </td>
                </tr>
              ) : (
                rows.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <code>{p.model}</code>
                    </td>
                    <td>{p.effectiveFrom}</td>
                    <td style={{ textAlign: 'right' }}>{p.inputUsdPerMillion}</td>
                    <td style={{ textAlign: 'right' }}>{p.outputUsdPerMillion}</td>
                    <td style={{ textAlign: 'right' }}>{p.cachedInputUsdPerMillion ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Button
                        type="button"
                        variant="secondary"
                        aria-label={`Delete ${p.model} price from ${p.effectiveFrom}`}
                        onClick={() => void onDelete(p)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div>
          <Button type="button" onClick={() => setAdding(true)}>
            Add price
          </Button>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'flex-start',
            maxWidth: 420,
          }}
        >
          <TextField
            label="Rupees per US dollar"
            inputMode="decimal"
            value={rate}
            onChange={(e) => {
              setRateValue(e.target.value);
              setRateError(undefined);
            }}
            error={rateError}
          />
          <div style={{ paddingTop: 'var(--space-5)' }}>
            <Button
              type="button"
              variant="secondary"
              disabled={setRate.isPending}
              onClick={() => void onSaveRate()}
            >
              Save rate
            </Button>
          </div>
        </div>
      </div>
      {adding ? <AddPriceModal models={modelsInUse} onClose={() => setAdding(false)} /> : null}
    </Card>
  );
}

/**
 * Admin → AI usage: what AI costs, per workspace, feature and day, from
 * Gemini's own token counts and the model price table.
 */
export function AiUsagePage() {
  const { isPlatformAdmin } = useAuth();
  const [preset, setPreset] = useState<Preset>('this-month');
  const [custom, setCustom] = useState(presetPeriod('this-month'));
  const period = preset === 'custom' ? custom : presetPeriod(preset);
  const report = useAiUsageReport(period);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  const r = report.data;
  const rate = r?.usdToInr ?? 88;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1100 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>AI usage</h1>
        <p style={{ margin: 'var(--space-1) 0 0', ...muted }}>
          Every Gemini call (quotations, catalogue import), from Gemini&apos;s own token counts.
          Cost uses the model price in force on the day of each call.
        </p>
      </div>

      <div
        style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <div className="ne-field">
          <label className="ne-field__label" htmlFor="ai-period">
            Period
          </label>
          <select
            id="ai-period"
            className="ne-input"
            value={preset}
            onChange={(e) => setPreset(e.target.value as Preset)}
          >
            <option value="this-month">This month</option>
            <option value="last-month">Last month</option>
            <option value="last-7">Last 7 days</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        {preset === 'custom' ? (
          <>
            <TextField
              label="From"
              type="date"
              value={custom.from}
              max={custom.to}
              onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })}
            />
            <TextField
              label="To"
              type="date"
              value={custom.to}
              min={custom.from}
              onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })}
            />
          </>
        ) : (
          <span style={{ ...muted, paddingBottom: 'var(--space-2)' }}>
            {period.from} to {period.to}
          </span>
        )}
      </div>

      {report.isPending ? (
        <PageState kind="loading" />
      ) : report.isError ? (
        <PageState
          kind="error"
          message={report.error.message}
          onRetry={() => void report.refetch()}
        />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gap: 'var(--space-3)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }}
          >
            <Stat
              label="AI cost"
              value={rupees(r!.totals.costUsd, rate)}
              sub={`${dollars(r!.totals.costUsd)} at ₹${rate}/$`}
            />
            <Stat
              label="AI calls"
              value={count(r!.totals.calls)}
              sub={r!.totals.failed ? `${r!.totals.failed} failed` : 'none failed'}
            />
            <Stat
              label="Tokens"
              value={tokens(r!.totals.totalTokens)}
              sub={`in ${tokens(r!.totals.inputTokens)} · out ${tokens(r!.totals.outputTokens)} · thinking ${tokens(r!.totals.thinkingTokens)}`}
            />
            <Stat
              label="Average per call"
              value={r!.totals.calls ? rupees(r!.totals.costUsd / r!.totals.calls, rate) : '—'}
            />
          </div>
          {r!.totals.uncostedCalls > 0 ? (
            <p style={{ margin: 0, color: 'var(--color-danger)' }}>
              {count(r!.totals.uncostedCalls)} calls have no price for their model yet, so the cost
              above leaves them out - add the price under Model prices.
            </p>
          ) : null}

          <Card title="By workspace">
            <ByTenant report={r!} />
          </Card>
          <Card title="By feature">
            <SumsTable
              label="Feature"
              rate={rate}
              rows={r!.byFeature.map((f) => ({
                ...f,
                key: f.feature,
                name: FEATURE_LABELS[f.feature] ?? f.feature,
              }))}
            />
          </Card>
          <Card title="By day">
            <SumsTable
              label="Day"
              rate={rate}
              rows={r!.byDay.map((d) => ({ ...d, key: d.day, name: d.day }))}
            />
          </Card>
        </>
      )}

      <PricesCard />

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/dashboard">Admin dashboard</Link>
      </p>
    </div>
  );
}
